import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useDesignerModeStore, useWorkflowStore } from '@flow-studio/store';
import type { FlowObserverTabContentProps } from '@flow-studio/flow-observer-core';
import { NodeDetailPanel } from './NodeDetailPanel.js';
import { useLocalStorage } from '../../hooks/useLocalStorage.js';

type NodeStatusFilter = 'all' | 'running' | 'completed' | 'failed' | 'waiting';

const STATUS_COLORS: Record<string, string> = {
  running: '#3b82f6',
  success: '#22c55e',
  completed: '#22c55e',
  failed: '#ef4444',
  error: '#ef4444',
  timeout: '#f59e0b',
  waiting: '#f59e0b',
  retrying: '#f59e0b',
  skipped: '#9ca3af',
  idle: '#d1d5db',
};

const PORT_COLORS: Record<string, string> = {
  success: '#22c55e',
  approved: '#22c55e',
  completed: '#22c55e',
  error: '#ef4444',
  failed: '#ef4444',
  rejected: '#ef4444',
  waiting: '#f59e0b',
  pending: '#f59e0b',
};

function getStatusColor(status: string): string {
  return STATUS_COLORS[status.toLowerCase()] ?? '#d1d5db';
}

function normalizeStatus(status: string): NodeStatusFilter | 'other' {
  const s = status.toLowerCase();
  if (s === 'running') return 'running';
  if (s === 'success' || s === 'completed') return 'completed';
  if (s === 'failed' || s === 'error' || s === 'timeout') return 'failed';
  if (s === 'waiting' || s === 'retrying') return 'waiting';
  return 'other';
}

function getPortColor(port?: string): string {
  if (!port) return '#9ca3af';
  return PORT_COLORS[port.toLowerCase()] ?? '#9ca3af';
}

const FILTER_LABELS: Record<NodeStatusFilter, string> = {
  all: 'All',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  waiting: 'Waiting',
};

export const ExecutionNodeListTabContent: React.FC<FlowObserverTabContentProps> = ({
  loadState,
  error,
  onRetry,
}) => {
  const nodeExecutionStates = useDesignerModeStore((s) => s.nodeExecutionStates);
  const setSelectedInspectorNodeKey = useDesignerModeStore((s) => s.setSelectedInspectorNodeKey);
  const selectedInspectorNodeKey = useDesignerModeStore((s) => s.selectedInspectorNodeKey);
  const nodeInspectorData = useDesignerModeStore((s) => s.nodeInspectorData);
  const executionState = useDesignerModeStore((s) => s.executionState);
  const canvasNodes = useWorkflowStore((s) => s.nodes);

  // SignalR events carry a human-readable name, but historical/replayed data or a
  // late-joining observer can miss it — fall back to the canvas node's displayName
  // (looked up by id), then to the raw execution key as a last resort.
  const canvasNodeNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const node of canvasNodes) {
      const name = node.data?.displayName || node.data?.nodeKey;
      if (name) map.set(node.id, name);
    }
    return map;
  }, [canvasNodes]);

  const getNodeDisplayName = useCallback(
    (nodeKey: string, label?: string) => label || canvasNodeNameById.get(nodeKey) || nodeKey,
    [canvasNodeNameById]
  );

  const [filter, setFilter] = useState<NodeStatusFilter>('all');

  // Drag-resize for the node-list/detail split — same pattern as ExecutionLogsTabContent's split.
  const [splitWidth, setSplitWidth] = useLocalStorage<number>('nodes-tab-split-width', 25);
  const [isResizing, setIsResizing] = useState(false);
  const splitRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback(() => {
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!splitRef.current) return;
      const rect = splitRef.current.getBoundingClientRect();
      const newWidth = ((e.clientX - rect.left) / rect.width) * 100;
      const clamped = Math.max(15, Math.min(85, newWidth));
      setSplitWidth(clamped);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, setSplitWidth]);

  const nodes = useMemo(() => {
    const entries = Array.from(nodeExecutionStates.entries());
    if (filter === 'all') return entries;
    return entries.filter(([, state]) => normalizeStatus(state.status) === filter);
  }, [nodeExecutionStates, filter]);

  if (loadState === 'loading') {
    return <div className="fop-loading">Loading…</div>;
  }

  if (loadState === 'error' && error) {
    return (
      <div className="fop-error" role="alert">
        <span>{error.message}</span>
        {error.retryable && (
          <button className="fop-error__retry" onClick={onRetry}>Retry</button>
        )}
      </div>
    );
  }

  // Inspector detail content. Deterministic (query layer over execution-logs) rather than the
  // nodeInspectorData live-overwrite cache — that cache is only used here for the port badge,
  // which is genuinely "whatever the last live event said" and has no deterministic equivalent.
  const inspectorDetail = (() => {
    if (!selectedInspectorNodeKey) {
      return (
        <div className="fop-empty">
          <span>Select a node to inspect it</span>
        </div>
      );
    }
    const cachedData = nodeInspectorData.get(selectedInspectorNodeKey);
    const portColor = getPortColor(cachedData?.outputPortKey);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontSize: '13px' }}>
        <div style={{ padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <span style={{ display: 'flex', alignItems: 'baseline', gap: '8px', minWidth: 0, flex: 1 }}>
            <span style={{ fontWeight: 600, color: '#d1d5db', flex: '0 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {getNodeDisplayName(selectedInspectorNodeKey, nodeExecutionStates.get(selectedInspectorNodeKey)?.label)}
            </span>
            <span style={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace', flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selectedInspectorNodeKey}
            </span>
          </span>
          {cachedData?.outputPortKey && (
            <span style={{
              padding: '2px 8px',
              borderRadius: '9999px',
              background: portColor,
              color: '#fff',
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'capitalize',
            }}>
              {cachedData.outputPortKey}
            </span>
          )}
          <button
            type="button"
            onClick={() => setSelectedInspectorNodeKey(null)}
            title="Close"
            aria-label="Close node detail"
            style={{
              background: 'none',
              border: 'none',
              color: '#9ca3af',
              cursor: 'pointer',
              fontSize: '16px',
              lineHeight: 1,
              padding: '2px 4px',
              flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>
        <div style={{ flex: 1, minHeight: 0 }}>
          <NodeDetailPanel nodeKey={selectedInspectorNodeKey} />
        </div>
      </div>
    );
  })();

  return (
    <div className="fop-exec-logs">
      {/* Filter toolbar — full width */}
      <div className="fop-logs-toolbar">
        {(Object.keys(FILTER_LABELS) as NodeStatusFilter[]).map((f) => (
          <button
            key={f}
            className={`fop-level-btn ${filter === f ? 'fop-level-btn--active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {FILTER_LABELS[f]}
          </button>
        ))}
        <span className="fop-logs-toolbar__spacer" />
        <span className="fop-logs-count">{nodes.length} nodes</span>
      </div>

      {/* Split: list left, detail right */}
      <div className="fop-logs-split" ref={splitRef} style={{ display: 'flex' }}>
        <div
          className="fop-logs-split__list"
          style={selectedInspectorNodeKey ? { flex: `0 0 ${splitWidth}%` } : { flex: '1', borderRight: 'none' }}
        >
          {nodeExecutionStates.size === 0 ? (
            <div className="fop-empty">
              <span>{'No nodes executed yet'}</span>
            </div>
          ) : nodes.length === 0 ? (
            <div className="fop-empty"><span>No nodes match the filter</span></div>
          ) : (
            <>
              <div className="fop-log-header-row">
                <div className="fop-log-header-cell fop-log-header-cell--flex">
                  <span>Node</span>
                </div>
                <div className="fop-log-header-cell" style={{ width: 90 }}>
                  <span>Status</span>
                </div>
              </div>
              <div className="fop-logs-scroll">
                {nodes.map(([nodeKey, state]) => {
                  const isSelected = selectedInspectorNodeKey === nodeKey;
                  const statusColor = getStatusColor(state.status);
                  const displayName = getNodeDisplayName(nodeKey, state.label);
                  return (
                    <button
                      key={nodeKey}
                      className={[
                        'fop-log-entry',
                        'fop-log-entry--clickable',
                        isSelected ? 'fop-log-entry--selected' : '',
                      ].filter(Boolean).join(' ')}
                      onClick={() => setSelectedInspectorNodeKey(nodeKey)}
                      title={`Inspect ${displayName} (${nodeKey})`}
                      aria-pressed={isSelected}
                      style={{ height: 28, display: 'flex', alignItems: 'center', gap: 0, paddingLeft: '8px' }}
                    >
                      <span className="fop-log-cell" style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{
                          width: '7px',
                          height: '7px',
                          borderRadius: '50%',
                          background: statusColor,
                          flexShrink: 0,
                          animation: state.status === 'running' ? 'hil-pulse 1s ease-in-out infinite' : undefined,
                        }} />
                        <span className="fop-log-message" style={{ flex: '0 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</span>
                        <span style={{ flex: '1 1 auto', minWidth: 0, fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nodeKey}</span>
                      </span>
                      <span className="fop-log-cell" style={{ width: 90, color: statusColor, fontWeight: 600, textTransform: 'capitalize' }}>
                        {state.status}
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Resizable divider */}
        {selectedInspectorNodeKey && (
          <div
            className="fop-logs-split__divider"
            onMouseDown={handleMouseDown}
            title="Drag to resize panels"
            role="separator"
            aria-label="Resize node list and details"
            style={{
              cursor: 'col-resize',
              flex: '0 0 2px',
              background: '#d1d5db',
              userSelect: 'none',
              transition: isResizing ? 'none' : 'background 0.2s',
            }}
            onMouseEnter={(e) => {
              if (!isResizing) (e.currentTarget as HTMLElement).style.background = '#9ca3af';
            }}
            onMouseLeave={(e) => {
              if (!isResizing) (e.currentTarget as HTMLElement).style.background = '#d1d5db';
            }}
          />
        )}

        {/* Inspector detail pane */}
        <div
          className="fop-logs-split__detail"
          style={selectedInspectorNodeKey ? { flex: `0 0 ${100 - splitWidth}%` } : { display: 'none' }}
        >
          {inspectorDetail}
        </div>
      </div>

      <style>{`
        @keyframes hil-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
};
